import { useState, useEffect, useCallback, useRef } from "react";
import type {
  ActiveToolCall,
  ChatMessage,
  PromptAttachmentMeta,
  PromptPayload,
  ChatSession,
  DebugLogEntry,
  PermissionRequest,
  SessionUpdate,
  SlashCommand,
  ToolPermissionPresets,
} from "@/types";
import { createPromptDisconnectWatchdog } from "@/lib/prompt-disconnect-watchdog";
import { prepareMessagesForPersistence } from "@/lib/chat-persistence";
import {
  findAutoApproveOptionForRequest,
  normalizeToolPermissionPresets,
  removePermissionPreset,
  upsertAllowPreset,
} from "@/lib/permission-presets";

interface UseChatOptions {
  activeSessionId: string | null;
  activeAcpSessionId: string | null;
  workingDirectory: string | null;
  agentId: string | null;
  modelId: string | null;
  onSessionCreated: (session: ChatSession) => void;
  onSessionUpdated: (
    id: string,
    messageCount: number,
    lastMessageAt: number,
  ) => void;
  /**
   * A prompt finished for a session that is not currently displayed —
   * switching sessions no longer cancels the in-flight turn, so its final
   * content arrives while another session is active. The host should persist
   * it against the owning session (keyed by ACP session id).
   */
  onBackgroundTurnCompleted?: (
    acpSessionId: string,
    finalContent: string,
    stopReason?: string,
  ) => void;
  pushDebugEntry: (entry: DebugLogEntry) => void;
  saveDebugEntries: (sessionId: string) => void;
}

const INVOKE_ERROR_PREFIX_RE = /^Error invoking remote method '[^']+':\s*/;

function getPromptErrorMessage(err: unknown): string {
  const rawMessage = err instanceof Error
    ? err.message
    : typeof err === "string"
      ? err
      : err && typeof err === "object" && typeof (err as { message?: unknown }).message === "string"
        ? (err as { message: string }).message
        : "";

  const normalized = rawMessage.replace(INVOKE_ERROR_PREFIX_RE, "").trim();
  return normalized || rawMessage || "Failed to send message";
}

export function useChat({
  activeSessionId,
  activeAcpSessionId,
  workingDirectory,
  agentId,
  modelId,
  onSessionCreated,
  onSessionUpdated,
  onBackgroundTurnCompleted,
  pushDebugEntry,
  saveDebugEntries,
}: UseChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingContent, setThinkingContent] = useState("");
  const [pendingPermission, setPendingPermission] =
    useState<PermissionRequest | null>(null);
  const [permissionPresets, setPermissionPresets] = useState<ToolPermissionPresets>({});
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [activeToolCall, setActiveToolCall] = useState<ActiveToolCall | null>(null);
  const [completedToolCalls, setCompletedToolCalls] = useState<Record<string, ActiveToolCall>>({});
  const [availableCommands, setAvailableCommands] = useState<SlashCommand[]>([]);
  const [isCancelling, setIsCancelling] = useState(false);
  const streamingContentRef = useRef("");
  const streamingThoughtRef = useRef("");
  const toolOutputsRef = useRef<Record<string, string>>({});
  const toolNamesRef = useRef<Record<string, string>>({});
  const runningToolCallIdsRef = useRef<string[]>([]);
  const activeToolCallIdRef = useRef<string | null>(null);
  const permissionPresetsRef = useRef<ToolPermissionPresets>({});
  permissionPresetsRef.current = permissionPresets;

  const failInFlightTurn = useCallback((errorMsg: string) => {
    streamingContentRef.current = "";
    streamingThoughtRef.current = "";
    toolOutputsRef.current = {};
    toolNamesRef.current = {};
    runningToolCallIdsRef.current = [];
    activeToolCallIdRef.current = null;

    setMessages((prev) => {
      const finalized = finalizeStreamingAgentMessage(prev);
      const markedTools = finalized.map((message) =>
        message.role === "tool" && message.toolStatus === "running"
          ? { ...message, toolStatus: "error" as const }
          : message,
      );
      return [
        ...markedTools,
        {
          id: crypto.randomUUID(),
          role: "agent",
          content: `Error: ${errorMsg}`,
          timestamp: Date.now(),
        },
      ];
    });
    setIsStreaming(false);
    setIsThinking(false);
    setThinkingContent("");
    setPendingPermission(null);
    setActiveTool(null);
    setActiveToolCall(null);
    setIsCancelling(false);
  }, []);

  function findStreamingMessageIndex(messages: ChatMessage[]): number {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role === "agent" && message.id === "streaming") {
        return index;
      }
    }
    return -1;
  }

  function setActiveToolFromId(toolCallId: string | null) {
    activeToolCallIdRef.current = toolCallId;
    if (!toolCallId) {
      setActiveTool(null);
      setActiveToolCall(null);
      return;
    }

    const toolName = toolNamesRef.current[toolCallId] || "tool";
    const output = toolOutputsRef.current[toolCallId] || "";
    setActiveTool(toolName);
    setActiveToolCall({
      toolCallId,
      toolName,
      status: "running",
      output,
    });
  }

  function upsertStreamingAgentMessage(
    prev: ChatMessage[],
    content: string,
    thinking?: string,
  ): ChatMessage[] {
    const streamingIndex = findStreamingMessageIndex(prev);

    if (streamingIndex === -1) {
      // A trailing agent message can only precede a fresh stream after
      // switching back to a session whose prompt kept running in the
      // background — normal turns always start after a user message. The
      // stream's fullContent extends that persisted partial, so absorb it
      // instead of rendering the same text twice.
      const last = prev[prev.length - 1];
      if (
        last
        && last.role === "agent"
        && last.content.length > 0
        && content.startsWith(last.content)
      ) {
        return [
          ...prev.slice(0, -1),
          {
            ...last,
            id: "streaming",
            content,
            thinking: thinking ?? last.thinking,
          },
        ];
      }

      return [
        ...prev,
        {
          id: "streaming",
          role: "agent",
          content,
          thinking,
          timestamp: Date.now(),
        },
      ];
    }

    return prev.map((message, index) =>
      index === streamingIndex
        ? { ...message, content, thinking }
        : message,
    );
  }

  function finalizeStreamingAgentMessage(
    prev: ChatMessage[],
    finalContent?: string,
  ): ChatMessage[] {
    const streamingIndex = findStreamingMessageIndex(prev);

    if (streamingIndex === -1) {
      // No live streaming message, but the turn's final content can still
      // extend a persisted partial from a background switch-away (the turn
      // ended before any post-restore chunk arrived).
      if (typeof finalContent === "string" && finalContent.length > 0) {
        const last = prev[prev.length - 1];
        if (
          last
          && last.role === "agent"
          && last.content !== finalContent
          && finalContent.startsWith(last.content)
        ) {
          return [
            ...prev.slice(0, -1),
            { ...last, id: crypto.randomUUID(), content: finalContent },
          ];
        }
      }
      return prev;
    }

    return prev.map((message, index) =>
      index === streamingIndex
        ? {
            ...message,
            id: crypto.randomUUID(),
            content:
              (typeof finalContent === "string" && finalContent.length > 0)
                ? finalContent
                : message.content,
          }
        : message,
    );
  }

  // Keep stable refs for callbacks used inside the IPC listener
  const isThinkingRef = useRef(false);
  isThinkingRef.current = isThinking;
  const activeSessionIdRef = useRef(activeSessionId);
  activeSessionIdRef.current = activeSessionId;
  const activeAcpSessionIdRef = useRef(activeAcpSessionId);
  activeAcpSessionIdRef.current = activeAcpSessionId;
  const onSessionUpdatedRef = useRef(onSessionUpdated);
  onSessionUpdatedRef.current = onSessionUpdated;
  const onBackgroundTurnCompletedRef = useRef(onBackgroundTurnCompleted);
  onBackgroundTurnCompletedRef.current = onBackgroundTurnCompleted;
  const pushDebugEntryRef = useRef(pushDebugEntry);
  pushDebugEntryRef.current = pushDebugEntry;
  const saveDebugEntriesRef = useRef(saveDebugEntries);
  saveDebugEntriesRef.current = saveDebugEntries;

  useEffect(() => {
    let cancelled = false;

    // The preload bridge can be absent on a failed/partial startup (or a
    // non-Electron context). This effect runs at mount on every render, so an
    // unguarded deref here white-screens the whole renderer via the error
    // boundary. Degrade to empty presets instead — same fallback as the catch.
    const api = window.freshvibe;
    if (!api) {
      setPermissionPresets({});
      return;
    }

    void api.getSettings()
      .then((settings) => {
        if (cancelled) return;
        setPermissionPresets(
          normalizeToolPermissionPresets(settings.permissionPresets),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setPermissionPresets({});
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Slash commands are agent-specific. Clear stale commands immediately when
  // the selected agent changes; the new agent can repopulate via ACP updates.
  useEffect(() => {
    setAvailableCommands([]);
  }, [agentId]);

  const resetTransientState = useCallback(() => {
    setIsStreaming(false);
    setIsThinking(false);
    setThinkingContent("");
    setPendingPermission(null);
    setActiveTool(null);
    setActiveToolCall(null);
    setCompletedToolCalls({});
    setAvailableCommands([]);
    setIsCancelling(false);
    streamingContentRef.current = "";
    streamingThoughtRef.current = "";
    toolOutputsRef.current = {};
    toolNamesRef.current = {};
    runningToolCallIdsRef.current = [];
    activeToolCallIdRef.current = null;
  }, []);

  useEffect(() => {
    if (!window.freshvibe?.onSessionUpdate || !window.freshvibe?.onPermissionRequest) {
      return;
    }

    function finishToolCallById(toolCallId: string, finalStatus: "completed" | "error") {
      const finalOutput = toolOutputsRef.current[toolCallId] || "";
      const toolName = toolNamesRef.current[toolCallId] || "tool";

      runningToolCallIdsRef.current = runningToolCallIdsRef.current.filter(
        (id) => id !== toolCallId,
      );

      if (finalOutput) {
        setCompletedToolCalls((m) => ({
          ...m,
          [toolCallId]: {
            toolCallId,
            toolName,
            status: finalStatus,
            output: finalOutput,
          },
        }));
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === `tool:${toolCallId}`
            ? { ...m, toolStatus: finalStatus, content: finalOutput }
            : m,
        ),
      );

      if (activeToolCallIdRef.current === toolCallId) {
        const nextActiveId =
          runningToolCallIdsRef.current[runningToolCallIdsRef.current.length - 1] || null;
        setActiveToolFromId(nextActiveId);
      }
    }

    const unsubSession = window.freshvibe.onSessionUpdate((data) => {
      const update = data as SessionUpdate;
      const updateSessionId = update.acpSessionId || null;
      const expectedSessionId = activeAcpSessionIdRef.current;

      if (updateSessionId) {
        const isForeign =
          (expectedSessionId && updateSessionId !== expectedSessionId)
          || (!expectedSessionId && Boolean(activeSessionIdRef.current));
        if (isForeign) {
          // The turn's final content would otherwise be lost — hand it to the
          // host so the background session's transcript stays complete.
          if (update.sessionUpdate === ("agent_message_end" as string)) {
            onBackgroundTurnCompletedRef.current?.(
              updateSessionId,
              typeof update.content === "string" ? update.content : "",
              (update as { stopReason?: string }).stopReason,
            );
          }
          return;
        }
      }

      // Forward every session update to the debug log
      pushDebugEntryRef.current({
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        level: "debug",
        source: "agent",
        message: `session_update: ${update.sessionUpdate}`,
        data,
      });

      switch (update.sessionUpdate) {
        case "agent_thought_chunk": {
          const chunk = typeof update.content === "string" ? update.content : "";
          streamingThoughtRef.current =
            update.fullThoughtContent ||
            streamingThoughtRef.current + chunk;
          setThinkingContent(streamingThoughtRef.current);
          setIsThinking(true);
          break;
        }

        case "agent_message_chunk": {
          // Agent started writing its response — thinking phase is over
          if (isThinkingRef.current) {
            setIsThinking(false);
          }

          const msgChunk = typeof update.content === "string" ? update.content : "";
          streamingContentRef.current =
            update.fullContent ||
            streamingContentRef.current + msgChunk;

          const thoughtSnapshot = streamingThoughtRef.current || undefined;

          setMessages((prev) =>
            upsertStreamingAgentMessage(
              prev,
              streamingContentRef.current,
              thoughtSnapshot,
            ),
          );
          setIsStreaming(true);
          break;
        }

        case "agent_message_end" as string: {
          setMessages((prev) => {
            const updated = finalizeStreamingAgentMessage(
              prev,
              typeof update.content === "string" ? update.content : undefined,
            );
            if (updated !== prev) {
              // Persist messages and debug log after agent turn ends
              const sid = activeSessionIdRef.current;
              if (sid) {
                window.freshvibe.saveSessionMessages(sid, updated);
                saveDebugEntriesRef.current(sid);
                onSessionUpdatedRef.current(
                  sid,
                  updated.length,
                  Date.now(),
                );
              }
            }
            return updated;
          });
          streamingContentRef.current = "";
          streamingThoughtRef.current = "";
          setIsStreaming(false);
          setIsThinking(false);
          setThinkingContent("");
          setIsCancelling(false);
          break;
        }

        case "tool_call": {
          const toolUpdate = update as { toolName?: string; toolCallId?: string; status?: string; kind?: string };
          const name = toolUpdate.toolName || toolUpdate.kind || "tool";
          const callId = toolUpdate.toolCallId || crypto.randomUUID();
          // ACP uses "in_progress" and "pending" for active states
          const isActive = toolUpdate.status === "in_progress" || toolUpdate.status === "pending";

          if (isActive) {
            toolOutputsRef.current[callId] = "";
            toolNamesRef.current[callId] = name;
            if (!runningToolCallIdsRef.current.includes(callId)) {
              runningToolCallIdsRef.current.push(callId);
            }
            setActiveToolFromId(callId);
            // Finalize the current streaming agent message (if any) so that
            // any subsequent agent_message_chunk text appears BELOW this tool
            // call rather than being appended to the message above it.
            // Reset the streaming content ref so a fresh message is created.
            if (streamingContentRef.current) {
              streamingContentRef.current = "";
            }
            // Insert a tool message into the stream
            setMessages((prev) => {
              // Finalize any in-flight streaming message first
              let updated = finalizeStreamingAgentMessage(prev);
              const existingIndex = updated.findIndex((message) => message.id === `tool:${callId}`);
              if (existingIndex !== -1) {
                return updated.map((message, index) =>
                  index === existingIndex
                    ? { ...message, toolName: name, toolStatus: "running" }
                    : message,
                );
              }

              return [
                ...updated,
                {
                  id: `tool:${callId}`,
                  role: "tool" as const,
                  content: "",
                  toolName: name,
                  toolStatus: "running",
                  timestamp: Date.now(),
                },
              ];
            });
          } else {
            // "completed", "failed", "error", "cancelled"
            toolNamesRef.current[callId] = name;
            const finalStatus = toolUpdate.status === "completed" ? "completed" as const : "error" as const;
            finishToolCallById(callId, finalStatus);
          }
          // Thinking stops when a tool starts
          if (isThinkingRef.current) {
            setIsThinking(false);
          }
          break;
        }

        case "tool_call_update": {
          const toolUpdatePayload = update as {
            content?: unknown;
            rawOutput?: Record<string, unknown>;
            status?: string;
            toolCallId?: string;
          };

          // Extract tool output text.
          // rawOutput (on completion) has clean stdout without markdown fences.
          // content (during streaming) is the full output so far, wrapped in
          // ```sh\n...\n``` fences. Content is a full replacement each time,
          // not a delta — so we replace toolOutputRef, not append.
          let text = "";
          const rawOut = toolUpdatePayload.rawOutput;
          if (rawOut) {
            text = String(
              rawOut.formatted_output || rawOut.aggregated_output || rawOut.stdout || "",
            );
          }
          if (!text) {
            const raw = toolUpdatePayload.content;
            if (typeof raw === "string") {
              text = raw;
            } else if (Array.isArray(raw)) {
              for (const block of raw) {
                if (block?.content?.type === "text") text += block.content.text;
                else if (block?.type === "text") text += block.text;
              }
            } else if (raw && typeof raw === "object") {
              const obj = raw as Record<string, unknown>;
              if (obj.type === "text") text = String(obj.text || "");
            }
            // Strip markdown code fences: ```sh\noutput\n``` → output
            text = text.replace(/^```[^\n]*\n([\s\S]*?)```\n?$/g, "$1");
          }
          if (text) {
            // Full replacement — each update is the complete output so far
            const toolCallId = toolUpdatePayload.toolCallId || activeToolCallIdRef.current;
            if (toolCallId) {
              toolOutputsRef.current[toolCallId] = text;
              if (activeToolCallIdRef.current === toolCallId) {
                setActiveToolCall((prev) =>
                  prev && prev.toolCallId === toolCallId
                    ? { ...prev, output: text }
                    : prev,
                );
              }
              setMessages((prev) => {
                const exists = prev.some((m) => m.id === `tool:${toolCallId}`);
                if (exists) {
                  return prev.map((m) =>
                    m.id === `tool:${toolCallId}`
                      ? { ...m, content: text }
                      : m,
                  );
                }
                // No preceding tool_call created this message — create it now
                // (handles agents that only send tool_call_update on completion)
                const toolName = toolNamesRef.current[toolCallId] || "tool";
                const updated = finalizeStreamingAgentMessage(prev);
                return [
                  ...updated,
                  {
                    id: `tool:${toolCallId}`,
                    role: "tool" as const,
                    content: text,
                    toolName,
                    toolStatus: "running" as const,
                    timestamp: Date.now(),
                  },
                ];
              });
            }
          }

          // tool_call_update can carry a completion status
          // ACP uses "completed", "failed", "error", or "cancelled"
          const tcStatus = toolUpdatePayload.status;
          const toolCallId = toolUpdatePayload.toolCallId || activeToolCallIdRef.current;
          if (tcStatus === "completed") {
            if (toolCallId) finishToolCallById(toolCallId, "completed");
          } else if (tcStatus === "failed" || tcStatus === "error" || tcStatus === "cancelled") {
            if (toolCallId) finishToolCallById(toolCallId, "error");
          }
          break;
        }

        case "available_commands_update": {
          const cmds = (update as { commands?: SlashCommand[] }).commands;
          setAvailableCommands(Array.isArray(cmds) ? cmds : []);
          break;
        }
      }
    });

    const unsubPermission = window.freshvibe.onPermissionRequest((data) => {
      const req = data as PermissionRequest;
      const reqSessionId = req.acpSessionId || null;
      const expectedSessionId = activeAcpSessionIdRef.current;
      if (reqSessionId) {
        if (expectedSessionId && reqSessionId !== expectedSessionId) {
          return;
        }
        if (!expectedSessionId && activeSessionIdRef.current) {
          return;
        }
      }
      pushDebugEntryRef.current({
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        level: "info",
        source: "agent",
        message: `permission_request: ${req.toolName}`,
        data: req,
      });
      // Save debug log immediately so permission events survive crashes/reloads
      const sid = activeSessionIdRef.current;
      if (sid) saveDebugEntriesRef.current(sid);

      const autoApprovedOption = findAutoApproveOptionForRequest(
        req,
        permissionPresetsRef.current,
      );
      if (autoApprovedOption) {
        pushDebugEntryRef.current({
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          level: "info",
          source: "agent",
          message: `permission_auto_response: ${req.toolName} -> ${autoApprovedOption.name}`,
          data: {
            permissionId: req.id,
            optionId: autoApprovedOption.optionId,
            optionKind: autoApprovedOption.kind,
          },
        });
        void window.freshvibe.respondToPermission(req.id, autoApprovedOption.optionId)
          .finally(() => {
            const activeSid = activeSessionIdRef.current;
            if (activeSid) saveDebugEntriesRef.current(activeSid);
          });
        return;
      }

      setPendingPermission(req);
    });
    return () => { unsubSession?.(); unsubPermission?.(); };
  }, []);

  const sendMessage = useCallback(
    async (payload: PromptPayload) => {
      const trimmedText = payload.text.trim();
      const attachmentSummaries: PromptAttachmentMeta[] = payload.attachments.map((attachment) => ({
        id: attachment.id,
        name: attachment.name,
        mimeType: attachment.mimeType,
        size: attachment.size,
        kind: attachment.kind,
      }));

      if ((!trimmedText && attachmentSummaries.length === 0) || isStreaming) return;

      // Auto-create a session on the first message if none is active
      let sessionId = activeSessionId;
      if (!sessionId) {
        const title =
          trimmedText.length > 0
            ? (trimmedText.length > 60 ? `${trimmedText.slice(0, 57)}...` : trimmedText)
            : (attachmentSummaries.length === 1
                ? attachmentSummaries[0].name
                : `Attachments (${attachmentSummaries.length})`);
        const now = Date.now();
        const newSession: ChatSession = {
          id: crypto.randomUUID(),
          title,
          projectFolder: workingDirectory || "Unknown",
          agentId: agentId || "unknown",
          modelId: modelId || undefined,
          messageCount: 0,
          lastMessageAt: now,
          createdAt: now,
        };
        sessionId = newSession.id;
        onSessionCreated(newSession);
      }

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: trimmedText,
        attachments: attachmentSummaries,
        timestamp: Date.now(),
      };

      setMessages((prev) => {
        const updated = [...prev, userMessage];
        if (sessionId) {
          window.freshvibe.saveSessionMessages(
            sessionId,
            prepareMessagesForPersistence(updated),
          );
          onSessionUpdated(sessionId, updated.length, userMessage.timestamp);
        }
        return updated;
      });
      setIsStreaming(true);
      streamingContentRef.current = "";
      streamingThoughtRef.current = "";
      setThinkingContent("");
      setIsThinking(false);
      const promptDisconnectWatchdog = createPromptDisconnectWatchdog({
        api: window.freshvibe,
        onTimeout: () => {
          void window.freshvibe.cancelPrompt().catch(() => {});
        },
      });

      try {
        await Promise.race([
          window.freshvibe.sendPrompt({
            text: trimmedText,
            attachments: payload.attachments,
          }),
          promptDisconnectWatchdog.promise,
        ]);
        setIsStreaming(false);
        setIsCancelling(false);
      } catch (err) {
        const errorMsg = getPromptErrorMessage(err);
        pushDebugEntryRef.current({
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          level: "error",
          source: "agent",
          message: `sendPrompt failed: ${errorMsg}`,
          data: { error: errorMsg, stack: err instanceof Error ? err.stack : undefined },
        });
        const sid = activeSessionIdRef.current;
        if (sid) saveDebugEntriesRef.current(sid);
        failInFlightTurn(errorMsg);
      } finally {
        promptDisconnectWatchdog.dispose();
      }
    },
    [
      activeSessionId,
      agentId,
      failInFlightTurn,
      isStreaming,
      modelId,
      onSessionCreated,
      workingDirectory,
    ],
  );

  const respondToPermission = useCallback(
    async (optionId: string | null) => {
      if (!pendingPermission) return;
      pushDebugEntryRef.current({
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        level: "info",
        source: "agent",
        message: `permission_response: ${optionId || "cancelled"}`,
        data: { permissionId: pendingPermission.id, optionId },
      });
      await window.freshvibe.respondToPermission(
        pendingPermission.id,
        optionId,
      );
      setPendingPermission(null);
      // Save debug log after permission response
      const sid = activeSessionIdRef.current;
      if (sid) saveDebugEntriesRef.current(sid);
    },
    [pendingPermission],
  );

  const alwaysApprovePendingPermission = useCallback(async () => {
    if (!pendingPermission) return;

    const nextPresets = upsertAllowPreset(
      permissionPresetsRef.current,
      pendingPermission,
    );
    setPermissionPresets(nextPresets);
    await window.freshvibe.saveSettings("permissionPresets", nextPresets);

    const autoApprovedOption = findAutoApproveOptionForRequest(
      pendingPermission,
      nextPresets,
    );
    if (!autoApprovedOption) return;

    pushDebugEntryRef.current({
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      level: "info",
      source: "agent",
      message: `permission_preset_saved: ${pendingPermission.toolName}`,
      data: {
        optionId: autoApprovedOption.optionId,
        optionKind: autoApprovedOption.kind,
      },
    });
    await respondToPermission(autoApprovedOption.optionId);
  }, [pendingPermission, respondToPermission]);

  const deletePermissionPreset = useCallback(async (toolName: string) => {
    const nextPresets = removePermissionPreset(permissionPresetsRef.current, toolName);
    setPermissionPresets(nextPresets);
    await window.freshvibe.saveSettings("permissionPresets", nextPresets);
  }, []);

  const cancelPrompt = useCallback(async () => {
    if (!isStreaming || isCancelling) return;
    setIsCancelling(true);
    try {
      await window.freshvibe.cancelPrompt();
    } catch {
      // Cancel is best-effort — if it fails the user can force-stop
    }
  }, [isStreaming, isCancelling]);

  const loadMessages = useCallback((msgs: ChatMessage[]) => {
    resetTransientState();
    setMessages(msgs);
  }, [resetTransientState]);

  const clearMessages = useCallback(() => {
    resetTransientState();
    setMessages([]);
  }, [resetTransientState]);

  return {
    messages,
    isStreaming,
    isThinking,
    isCancelling,
    thinkingContent,
    pendingPermission,
    permissionPresets,
    activeTool,
    activeToolCall,
    completedToolCalls,
    availableCommands,
    sendMessage,
    cancelPrompt,
    respondToPermission,
    alwaysApprovePendingPermission,
    deletePermissionPreset,
    loadMessages,
    clearMessages,
  };
}
